import React, { useEffect, useState } from "react";
import axios from "axios";
import Link from "next/link";
import { stringify } from "qs";

import consumerApi from "@/api/services/consumer-api";
import { Action, Category } from "@/constants/events";
import {
  MenuItem,
  MenuItemFilter,
  MenuItemSort,
} from "@/custom-types/MenuItem";
import { MenuResponse } from "@/custom-types/Search";
import useShopSearchParams from "@/hooks/useShopSearchParams";
import { useSelector } from "@/redux/hooks";
import { getCoordinates } from "@/redux/selectors/location";
import logError from "@/utils/logError";

import Carousel from "@/components/botanic/Carousel";
import MenuItemCard, {
  MenuItemCardPlaceholder,
} from "@/components/botanic/MenuItemCard";
import TrackImpression from "@/components/TrackImpression";

import { useEventTracker } from "../../../hooks/useEventTracker";
import { MenuItemCardActions } from "./MenuItemCardActions";

type Props = {
  actionHref?: string;
  actionOnClick?: (e: React.KeyboardEvent | React.MouseEvent) => void;
  actionText?: string;
  cardOnClick?: (menuItem: MenuItem) => void;
  cardsPerSlide?: number;
  className?: string;
  filter?: MenuItemFilter;
  header: string;
  headerLink?: string;
  headerOnClick?: () => void;
  query?: string | string[] | undefined;
  showOnlineOrdering?: boolean;
  slug?: string;
  sort?: MenuItemSort;
  subheader?: string;
  viewDetailsOnClick?: (menuItem: MenuItem) => void;
};

const MenuItemCarousel: React.FC<Props> = ({
  actionHref,
  actionOnClick = () => {},
  actionText,
  cardOnClick = () => {},
  cardsPerSlide = 4,
  className,
  filter = {},
  header,
  headerLink,
  headerOnClick = () => {},
  query,
  showOnlineOrdering,
  slug,
  sort,
  subheader,
  viewDetailsOnClick = () => {},
}) => {
  const [loading, setLoading] = useState(true);
  const [menuItems, setMenuItems] = useState<MenuItem[]>([]);
  const [totalCount, setTotalCount] = useState<number | null>(null);

  const { publishEvent } = useEventTracker();

  const coordinates = useSelector(getCoordinates);

  const { shopCategory, showSimilar, shopName } = useShopSearchParams();

  const showSimilarStrains =
    shopCategory?.includes("strain") &&
    (showSimilar === "on" || showSimilar === "menu_items_only");

  useEffect(() => {
    const source = axios.CancelToken.source();

    const fetchMenuItems = async () => {
      try {
        setLoading(true);

        const boostQuery = showSimilarStrains
          ? { boost: "similar_menu_items" }
          : {};
        const queryString = stringify(
          {
            filter,
            q: query,
            sort: sort,
            take: 8,
            ...boostQuery,
            ...coordinates,
          },
          { arrayFormat: "brackets", encodeValuesOnly: true },
        );

        const response = await consumerApi.get<MenuResponse>(
          `/api/dispensaries/v1/${slug}/menu_items?${queryString}`,
          {
            cancelToken: source.token,
          },
        );

        const {
          data,
          metadata: { totalCount },
        } = response.data;

        setLoading(false);
        setMenuItems(data);
        setTotalCount(totalCount);
      } catch (e) {
        if (e.statusCode !== "ERR_CANCELED") {
          logError(e.message, {
            functionName: "MenuItemCarousel",
            service: "consumer",
            statusCode: e.statusCode,
          });
        }

        setLoading(false);
        setMenuItems([]);
        setTotalCount(null);
      }
    };

    fetchMenuItems();

    return () => {
      // Cancel the axios request
      source.cancel();
    };
  }, [
    JSON.stringify(coordinates),
    JSON.stringify(filter),
    JSON.stringify(query),
    JSON.stringify(sort),
    showSimilarStrains,
    slug,
  ]);

  const updatedActionText = actionText?.replace(
    "%tc",
    totalCount?.toString() || "all",
  );

  const formattedSubheader = (
    <>
      {subheader ? <span className="mr-md">{subheader}</span> : null}

      {showOnlineOrdering ? (
        <span className="text-notification">Free online ordering</span>
      ) : (
        <span className="text-grey">No Leafly online ordering</span>
      )}
    </>
  );

  return (
    <Carousel
      className={className}
      cardsPerSlide={cardsPerSlide}
      title={
        <h2>
          {headerLink ? (
            <Link
              href={headerLink}
              onClick={headerOnClick}
              className="heading--m"
              data-testid="carousel-title-link"
            >
              {header}
            </Link>
          ) : (
            header
          )}
        </h2>
      }
      subtitle={
        <span className="block text-xs font-bold text-deep-green">
          {formattedSubheader}
        </span>
      }
      seeMoreHref={actionHref}
      seeMoreText={updatedActionText}
      seeMoreOnClick={actionOnClick}
      data-testid="menu-carousel"
    >
      {loading
        ? new Array(6)
            .fill(null)
            .map((_, i) => (
              <MenuItemCardPlaceholder key={`menu-item-carousel-card-${i}`} />
            ))
        : menuItems.map((menuItem) => (
            <TrackImpression
              key={menuItem.id}
              className="h-full"
              trackingFunction={() =>
                publishEvent({
                  action: Action.impression,
                  brandId: menuItem.brand?.id,
                  category: Category.menuItemCard,
                  dispensaryId: menuItem.dispensary?.id,
                  label: "product card",
                  menuItemId: menuItem.menuItemId,
                  productId: menuItem.product?.id,
                  strainId: menuItem.strain?.id,
                })
              }
            >
              <MenuItemCard
                handleCardClick={() => cardOnClick(menuItem)}
                menuItem={menuItem}
                similarStrain={
                  showSimilarStrains && menuItem?.strain?.name !== shopName
                }
              >
                <MenuItemCardActions
                  menuItem={menuItem}
                  viewDetailsOnClick={() => viewDetailsOnClick(menuItem)}
                />
              </MenuItemCard>
            </TrackImpression>
          ))}
    </Carousel>
  );
};

export default MenuItemCarousel;
